/**
 * CCTV cameras — admin registry (list + grid + add/edit dialog).
 *
 * Surfaces:
 *   - KPI strip (total / active / offline / maintenance)
 *   - Filters: placement + status + search
 *   - Grid view (default): camera cards with last snapshot thumbnail, name,
 *     status pill, last ping. Toggle to List for table density.
 *   - Per camera: View / Test ping / Capture snapshot / View credentials /
 *     Edit / Delete.
 *   - Add Camera dialog with full form (sectioned: Location, Hardware,
 *     Stream config — creds masked by default, Recording).
 *
 * Credentials section is mask-by-default with a "Show" toggle. The actual
 * decrypted creds come from the dedicated /credentials endpoint which
 * audits every reveal (camera.creds_view).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Camera as CameraIcon,
  PlusCircle,
  Activity,
  ShieldAlert,
  Wrench,
  ServerCrash,
  Eye,
  EyeOff,
  KeyRound,
  Search,
  Trash2,
  RefreshCw,
  Camera as CameraLens,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { TabsPills } from '@/components/ui/tabs-pills';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatRelative } from '@/lib/format';

export type CameraStatus = 'active' | 'offline' | 'maintenance' | 'decommissioned';
export type CameraPlacement =
  | 'main_gate'
  | 'rear_gate'
  | 'parking_basement'
  | 'parking_open'
  | 'lobby'
  | 'lift'
  | 'staircase'
  | 'common_area'
  | 'rooftop'
  | 'perimeter'
  | 'other';
export type CameraType =
  | 'ip_rtsp'
  | 'ip_http'
  | 'hikvision_nvr'
  | 'cp_plus_nvr'
  | 'hybrid_dvr'
  | 'unknown';

export interface Camera {
  id: string;
  name: string;
  placement: CameraPlacement;
  location_label: string | null;
  camera_type: CameraType;
  brand: string | null;
  model: string | null;
  resolution: string | null;
  stream_url: string | null;
  snapshot_url: string | null;
  status: CameraStatus;
  has_credentials: boolean;
  last_ping_at: string | null;
  last_ping_ok: boolean | null;
  consecutive_ping_failures: number;
  has_ptz: boolean;
  has_audio: boolean;
  has_night_vision: boolean;
  recording_enabled: boolean;
  storage_days: number | null;
  nvr_channel_no: number | null;
  installed_at: string | null;
  warranty_until: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface CursorList<T> {
  items: T[];
  next_cursor: string | null;
}

const PLACEMENT_LABEL: Record<CameraPlacement, string> = {
  main_gate: 'Main gate',
  rear_gate: 'Rear gate',
  parking_basement: 'Parking — basement',
  parking_open: 'Parking — open',
  lobby: 'Lobby',
  lift: 'Lift',
  staircase: 'Staircase',
  common_area: 'Common area',
  rooftop: 'Rooftop',
  perimeter: 'Perimeter',
  other: 'Other'
};

const TYPE_LABEL: Record<CameraType, string> = {
  ip_rtsp: 'IP — RTSP',
  ip_http: 'IP — HTTP',
  hikvision_nvr: 'Hikvision NVR',
  cp_plus_nvr: 'CP Plus NVR',
  hybrid_dvr: 'Hybrid DVR',
  unknown: 'Unknown'
};

const STATUS_VARIANT: Record<CameraStatus, 'ok' | 'alert' | 'warn' | 'muted'> = {
  active: 'ok',
  offline: 'alert',
  maintenance: 'warn',
  decommissioned: 'muted'
};

const STATUS_LABEL: Record<CameraStatus, string> = {
  active: 'Active',
  offline: 'Offline',
  maintenance: 'Maintenance',
  decommissioned: 'Decommissioned'
};

type ViewMode = 'grid' | 'list';

export function CamerasPage(): JSX.Element {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<CameraStatus | 'all'>('all');
  const [placementFilter, setPlacementFilter] = useState<CameraPlacement | ''>('');
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [editTarget, setEditTarget] = useState<Camera | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [credTarget, setCredTarget] = useState<Camera | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Camera | null>(null);

  const filtersHash = useMemo(
    () => JSON.stringify({ statusFilter, placementFilter, search }),
    [statusFilter, placementFilter, search]
  );
  useMemo(() => setCursor(null), [filtersHash]);

  const listQuery = useQuery({
    queryKey: ['cameras', { statusFilter, placementFilter, search, cursor }],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (statusFilter !== 'all') qs.set('status', statusFilter);
      if (placementFilter) qs.set('placement', placementFilter);
      if (search) qs.set('q', search);
      if (cursor) qs.set('cursor', cursor);
      return get<CursorList<Camera>>(`/cameras?${qs.toString()}`);
    },
    placeholderData: (prev) => prev
  });

  const { items, nextCursor } = useCursorAccumulator(listQuery.data, cursor, filtersHash);

  // KPIs are derived from the in-page list view. For the very first page we
  // also pull a small "all" count via the same endpoint with limit=1 just to
  // populate the strip — keeping it simple here, computed from the loaded
  // items. (Production would expose a /cameras/stats endpoint; deferred.)
  const kpis = useMemo(() => {
    const total = items.length;
    const counts: Record<CameraStatus, number> = {
      active: 0,
      offline: 0,
      maintenance: 0,
      decommissioned: 0
    };
    items.forEach((c) => {
      counts[c.status] += 1;
    });
    return { total, ...counts };
  }, [items]);

  const pingMutation = useMutation({
    mutationFn: (id: string) =>
      post<{ ok: boolean; status: number | null; ms: number; camera_status: CameraStatus }>(
        `/cameras/${id}/ping`,
        {}
      ),
    onSuccess: (data) => {
      toast.success(
        data.ok
          ? `Ping OK (${data.ms} ms, HTTP ${data.status ?? '?'})`
          : `Ping failed (${data.ms} ms)`
      );
      void qc.invalidateQueries({ queryKey: ['cameras'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const snapshotMutation = useMutation({
    mutationFn: (id: string) =>
      post<{ id: string }>(`/cameras/${id}/snapshot`, { capture_reason: 'manual' }),
    onSuccess: () => {
      toast.success('Snapshot captured');
      void qc.invalidateQueries({ queryKey: ['camera-snapshots'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => del<void>(`/cameras/${id}`),
    onSuccess: () => {
      toast.success('Camera deleted');
      setDeleteTarget(null);
      void qc.invalidateQueries({ queryKey: ['cameras'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <PageHeader
      title="CCTV cameras"
      description="Inventory of all cameras across gates, parking, and common areas. Manage RTSP credentials, capture evidence snapshots, monitor health."
      actions={
        <Button onClick={() => setAddOpen(true)}>
          <PlusCircle className="h-4 w-4" />
          Add camera
        </Button>
      }
    >
      {/* KPI strip */}
      <div className="mb-vt-5 grid grid-cols-2 gap-vt-3 sm:grid-cols-4">
        <KpiTile label="Total" value={kpis.total} tone="info" icon={<CameraIcon className="h-4 w-4" />} loading={listQuery.isLoading} />
        <KpiTile label="Active" value={kpis.active} tone="ok" icon={<Activity className="h-4 w-4" />} loading={listQuery.isLoading} />
        <KpiTile label="Offline" value={kpis.offline} tone="alert" icon={<ServerCrash className="h-4 w-4" />} loading={listQuery.isLoading} />
        <KpiTile label="Maintenance" value={kpis.maintenance} tone="warn" icon={<Wrench className="h-4 w-4" />} loading={listQuery.isLoading} />
      </div>

      {/* Filters */}
      <div className="mb-vt-4 flex flex-wrap items-end gap-vt-3">
        <TabsPills
          aria-label="Status filter"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as CameraStatus | 'all')}
          options={[
            { value: 'all', label: 'All' },
            { value: 'active', label: 'Active' },
            { value: 'offline', label: 'Offline' },
            { value: 'maintenance', label: 'Maintenance' },
            { value: 'decommissioned', label: 'Decommissioned' }
          ]}
        />
        <div className="flex flex-col gap-vt-1">
          <Label htmlFor="cam-placement">Placement</Label>
          <Select
            id="cam-placement"
            value={placementFilter}
            onChange={(e) => setPlacementFilter(e.target.value as CameraPlacement | '')}
            className="w-52"
          >
            <option value="">All placements</option>
            {Object.entries(PLACEMENT_LABEL).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-vt-1">
          <Label htmlFor="cam-search">Search</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-vt-text-muted" />
            <Input
              id="cam-search"
              placeholder="Name, brand, location…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64 pl-8"
            />
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1 rounded-vt-pill border border-vt-border bg-vt-surface p-1">
          <button
            type="button"
            onClick={() => setViewMode('grid')}
            className={`rounded-vt-pill px-3 py-1 text-vt-12 ${viewMode === 'grid' ? 'bg-vt-primary-100 text-vt-primary' : 'text-vt-text-muted'}`}
          >
            Grid
          </button>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className={`rounded-vt-pill px-3 py-1 text-vt-12 ${viewMode === 'list' ? 'bg-vt-primary-100 text-vt-primary' : 'text-vt-text-muted'}`}
          >
            List
          </button>
        </div>
      </div>

      {/* Body */}
      {listQuery.isLoading ? (
        <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      ) : listQuery.isError ? (
        <Card>
          <CardContent className="text-vt-alert">{getErrorMessage(listQuery.error)}</CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<CameraIcon className="h-7 w-7" />}
              title="No cameras registered"
              description="Add your first CCTV camera to enable guard live-view, evidence snapshots, and health checks."
              action={
                <Button onClick={() => setAddOpen(true)}>
                  <PlusCircle className="h-4 w-4" />
                  Add camera
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <CameraCard
              key={c.id}
              camera={c}
              busyPingId={pingMutation.variables ?? null}
              pingPending={pingMutation.isPending}
              snapPending={snapshotMutation.isPending}
              snapBusyId={snapshotMutation.variables ?? null}
              onPing={() => pingMutation.mutate(c.id)}
              onSnapshot={() => snapshotMutation.mutate(c.id)}
              onEdit={() => setEditTarget(c)}
              onCreds={() => setCredTarget(c)}
              onDelete={() => setDeleteTarget(c)}
            />
          ))}
        </div>
      ) : (
        <CameraTable
          items={items}
          onPing={(c) => pingMutation.mutate(c.id)}
          onSnapshot={(c) => snapshotMutation.mutate(c.id)}
          onEdit={(c) => setEditTarget(c)}
          onCreds={(c) => setCredTarget(c)}
          onDelete={(c) => setDeleteTarget(c)}
        />
      )}

      <CursorPaginator
        hasMore={Boolean(nextCursor)}
        loading={listQuery.isFetching}
        itemsShown={items.length}
        onLoadMore={() => nextCursor && setCursor(nextCursor)}
      />

      {addOpen ? <CameraDialog onClose={() => setAddOpen(false)} /> : null}
      {editTarget ? (
        <CameraDialog camera={editTarget} onClose={() => setEditTarget(null)} />
      ) : null}
      {credTarget ? (
        <CredentialsDialog camera={credTarget} onClose={() => setCredTarget(null)} />
      ) : null}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete camera?"
        description={
          deleteTarget
            ? `Permanently delete "${deleteTarget.name}". Captured snapshots will be removed too.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        loading={deleteMutation.isPending}
      />
    </PageHeader>
  );
}

// ──────────────────────── sub-components ────────────────────────

function KpiTile({
  label,
  value,
  tone,
  icon,
  loading
}: {
  label: string;
  value: number;
  tone: 'info' | 'ok' | 'alert' | 'warn';
  icon: React.ReactNode;
  loading?: boolean;
}): JSX.Element {
  const TONE_CLS: Record<typeof tone, string> = {
    info: 'bg-vt-info-100 text-vt-info',
    ok: 'bg-vt-safe-100 text-vt-safe',
    alert: 'bg-vt-alert-100 text-vt-alert',
    warn: 'bg-vt-warn-100 text-vt-warn'
  };
  return (
    <Card>
      <CardContent className="p-vt-4">
        <div className="mb-vt-2 inline-flex items-center gap-vt-2 rounded-vt-pill px-2 py-1 text-vt-12 font-medium ring-1 ring-inset ring-vt-border">
          <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${TONE_CLS[tone]}`}>
            {icon}
          </span>
          {label}
        </div>
        <div className="text-vt-24 font-semibold text-vt-text">
          {loading ? <Skeleton className="h-7 w-16" /> : value}
        </div>
      </CardContent>
    </Card>
  );
}

function CameraCard({
  camera,
  busyPingId,
  pingPending,
  snapPending,
  snapBusyId,
  onPing,
  onSnapshot,
  onEdit,
  onCreds,
  onDelete
}: {
  camera: Camera;
  busyPingId: string | null;
  pingPending: boolean;
  snapPending: boolean;
  snapBusyId: string | null;
  onPing: () => void;
  onSnapshot: () => void;
  onEdit: () => void;
  onCreds: () => void;
  onDelete: () => void;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="flex flex-col gap-vt-3 p-vt-4">
        <div className="flex items-start justify-between gap-vt-3">
          <div className="min-w-0">
            <Link
              to="/cameras/$id"
              params={{ id: camera.id }}
              className="block truncate text-vt-14 font-semibold text-vt-text hover:underline"
            >
              {camera.name}
            </Link>
            <div className="text-vt-12 text-vt-text-muted">{PLACEMENT_LABEL[camera.placement]}</div>
          </div>
          <Badge variant={STATUS_VARIANT[camera.status]}>{STATUS_LABEL[camera.status]}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-vt-2 text-vt-12 text-vt-text-muted">
          <div>
            <div className="text-vt-text-soft">Type</div>
            <div className="text-vt-text">{TYPE_LABEL[camera.camera_type]}</div>
          </div>
          <div>
            <div className="text-vt-text-soft">Brand</div>
            <div className="text-vt-text">
              {camera.brand ?? '—'}
              {camera.resolution ? ` · ${camera.resolution}` : ''}
            </div>
          </div>
          <div>
            <div className="text-vt-text-soft">Last ping</div>
            <div className="text-vt-text">
              {camera.last_ping_at ? formatRelative(camera.last_ping_at) : '—'}
              {camera.last_ping_at && camera.last_ping_ok === false ? ' · failed' : ''}
            </div>
          </div>
          <div>
            <div className="text-vt-text-soft">Creds</div>
            <div className="text-vt-text">{camera.has_credentials ? 'Stored' : 'None'}</div>
          </div>
        </div>
        {camera.consecutive_ping_failures >= 2 && camera.status !== 'offline' ? (
          <div className="flex items-center gap-vt-2 rounded-vt-sm border border-vt-warn/40 bg-vt-warn-100 p-vt-2 text-vt-12 text-vt-warn">
            <AlertTriangle className="h-4 w-4" />
            {camera.consecutive_ping_failures} consecutive ping failures
          </div>
        ) : null}
        <div className="flex flex-wrap gap-vt-2 pt-vt-1">
          <Button size="sm" variant="ghost" onClick={onPing} disabled={pingPending && busyPingId === camera.id}>
            <RefreshCw className="h-3.5 w-3.5" />
            Test ping
          </Button>
          <Button size="sm" variant="ghost" onClick={onSnapshot} disabled={snapPending && snapBusyId === camera.id}>
            <CameraLens className="h-3.5 w-3.5" />
            Snapshot
          </Button>
          <Button size="sm" variant="ghost" onClick={onCreds}>
            <KeyRound className="h-3.5 w-3.5" />
            Credentials
          </Button>
          <Button size="sm" variant="ghost" onClick={onEdit}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-vt-alert">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CameraTable({
  items,
  onPing,
  onSnapshot,
  onEdit,
  onCreds,
  onDelete
}: {
  items: Camera[];
  onPing: (c: Camera) => void;
  onSnapshot: (c: Camera) => void;
  onEdit: (c: Camera) => void;
  onCreds: (c: Camera) => void;
  onDelete: (c: Camera) => void;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-vt-13">
            <thead className="bg-vt-surface-2 text-vt-text-muted">
              <tr className="[&>th]:px-vt-3 [&>th]:py-vt-2 [&>th]:text-left">
                <th>Name</th>
                <th>Placement</th>
                <th>Type / brand</th>
                <th>Status</th>
                <th>Last ping</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="[&>tr]:border-t [&>tr]:border-vt-border">
              {items.map((c) => (
                <tr key={c.id} className="[&>td]:px-vt-3 [&>td]:py-vt-2">
                  <td>
                    <Link
                      to="/cameras/$id"
                      params={{ id: c.id }}
                      className="font-medium text-vt-text hover:underline"
                    >
                      {c.name}
                    </Link>
                  </td>
                  <td>{PLACEMENT_LABEL[c.placement]}</td>
                  <td>
                    <div>{TYPE_LABEL[c.camera_type]}</div>
                    <div className="text-vt-12 text-vt-text-muted">{c.brand ?? '—'}</div>
                  </td>
                  <td>
                    <Badge variant={STATUS_VARIANT[c.status]}>{STATUS_LABEL[c.status]}</Badge>
                  </td>
                  <td className="text-vt-text-muted">
                    {c.last_ping_at ? formatRelative(c.last_ping_at) : '—'}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-vt-1">
                      <Button size="sm" variant="ghost" onClick={() => onPing(c)}>Ping</Button>
                      <Button size="sm" variant="ghost" onClick={() => onSnapshot(c)}>Snapshot</Button>
                      <Button size="sm" variant="ghost" onClick={() => onCreds(c)}>Creds</Button>
                      <Button size="sm" variant="ghost" onClick={() => onEdit(c)}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(c)} className="text-vt-alert">
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

interface CameraFormState {
  name: string;
  placement: CameraPlacement;
  location_label: string;
  camera_type: CameraType;
  brand: string;
  model: string;
  resolution: string;
  stream_url: string;
  username: string;
  password: string;
  snapshot_url: string;
  status: CameraStatus;
  has_ptz: boolean;
  has_audio: boolean;
  has_night_vision: boolean;
  recording_enabled: boolean;
  storage_days: string;
  nvr_channel_no: string;
  notes: string;
}

function blankForm(c?: Camera | null): CameraFormState {
  return {
    name: c?.name ?? '',
    placement: c?.placement ?? 'main_gate',
    location_label: c?.location_label ?? '',
    camera_type: c?.camera_type ?? 'ip_rtsp',
    brand: c?.brand ?? '',
    model: c?.model ?? '',
    resolution: c?.resolution ?? '',
    stream_url: c?.stream_url ?? '',
    username: '',
    password: '',
    snapshot_url: c?.snapshot_url ?? '',
    status: c?.status ?? 'active',
    has_ptz: c?.has_ptz ?? false,
    has_audio: c?.has_audio ?? false,
    has_night_vision: c?.has_night_vision ?? true,
    recording_enabled: c?.recording_enabled ?? false,
    storage_days: c?.storage_days != null ? String(c.storage_days) : '',
    nvr_channel_no: c?.nvr_channel_no != null ? String(c.nvr_channel_no) : '',
    notes: c?.notes ?? ''
  };
}

function CameraDialog({
  camera,
  onClose
}: {
  camera?: Camera;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const isEdit = Boolean(camera);
  const [form, setForm] = useState<CameraFormState>(blankForm(camera));
  const [showPassword, setShowPassword] = useState(false);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        placement: form.placement,
        location_label: form.location_label.trim() || null,
        camera_type: form.camera_type,
        brand: form.brand.trim() || null,
        model: form.model.trim() || null,
        resolution: form.resolution.trim() || null,
        stream_url: form.stream_url.trim() || null,
        snapshot_url: form.snapshot_url.trim() || null,
        status: form.status,
        has_ptz: form.has_ptz,
        has_audio: form.has_audio,
        has_night_vision: form.has_night_vision,
        recording_enabled: form.recording_enabled,
        storage_days: form.storage_days ? Number(form.storage_days) : null,
        nvr_channel_no: form.nvr_channel_no ? Number(form.nvr_channel_no) : null,
        notes: form.notes.trim() || null
      };
      // Only include username/password if user typed something. On edit, we
      // never round-trip the previous value (creds are encrypted at rest and
      // never exposed to this form); leaving blank means "no change".
      if (form.username) body.username = form.username;
      if (form.password) body.password = form.password;
      if (isEdit && camera) {
        return patch<Camera>(`/cameras/${camera.id}`, body);
      }
      return post<Camera>('/cameras', body);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Camera updated' : 'Camera added');
      void qc.invalidateQueries({ queryKey: ['cameras'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit camera' : 'Add camera'}</DialogTitle>
          <DialogDescription>
            Configure stream + credentials. Username / password are encrypted at rest and only
            decrypted when an admin explicitly requests them.
          </DialogDescription>
        </DialogHeader>

        <Section title="Location">
          <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2">
            <Field label="Name *">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Main gate — entry side"
                maxLength={120}
              />
            </Field>
            <Field label="Placement *">
              <Select
                value={form.placement}
                onChange={(e) => setForm({ ...form, placement: e.target.value as CameraPlacement })}
              >
                {Object.entries(PLACEMENT_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Location label">
              <Input
                value={form.location_label}
                onChange={(e) => setForm({ ...form, location_label: e.target.value })}
                placeholder="e.g. Above intercom — Block A"
                maxLength={200}
              />
            </Field>
            <Field label="Status">
              <Select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as CameraStatus })}
              >
                <option value="active">Active</option>
                <option value="offline">Offline</option>
                <option value="maintenance">Maintenance</option>
                <option value="decommissioned">Decommissioned</option>
              </Select>
            </Field>
          </div>
        </Section>

        <Section title="Hardware">
          <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2">
            <Field label="Camera type *">
              <Select
                value={form.camera_type}
                onChange={(e) => setForm({ ...form, camera_type: e.target.value as CameraType })}
              >
                {Object.entries(TYPE_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Brand">
              <Input
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
                placeholder="Hikvision / CP Plus / Dahua"
              />
            </Field>
            <Field label="Model">
              <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            </Field>
            <Field label="Resolution">
              <Select
                value={form.resolution}
                onChange={(e) => setForm({ ...form, resolution: e.target.value })}
              >
                <option value="">—</option>
                <option value="720p">720p</option>
                <option value="1080p">1080p</option>
                <option value="2K">2K</option>
                <option value="4K">4K</option>
              </Select>
            </Field>
          </div>
          <div className="mt-vt-3 flex flex-wrap gap-vt-3 text-vt-13">
            <CheckRow label="PTZ" checked={form.has_ptz} onChange={(v) => setForm({ ...form, has_ptz: v })} />
            <CheckRow label="Audio" checked={form.has_audio} onChange={(v) => setForm({ ...form, has_audio: v })} />
            <CheckRow label="Night vision" checked={form.has_night_vision} onChange={(v) => setForm({ ...form, has_night_vision: v })} />
          </div>
        </Section>

        <Section title="Stream config">
          <div className="grid grid-cols-1 gap-vt-3">
            <Field label="Stream URL (RTSP / HTTP)">
              <Input
                value={form.stream_url}
                onChange={(e) => setForm({ ...form, stream_url: e.target.value })}
                placeholder="rtsp://192.168.1.10:554/stream1 or https://…"
                maxLength={500}
              />
            </Field>
            <Field label="Snapshot URL">
              <Input
                value={form.snapshot_url}
                onChange={(e) => setForm({ ...form, snapshot_url: e.target.value })}
                placeholder="http://192.168.1.10/cgi-bin/snapshot.cgi"
                maxLength={500}
              />
            </Field>
            <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2">
              <Field label={isEdit ? 'New username (leave blank to keep)' : 'Username'}>
                <Input
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  autoComplete="off"
                />
              </Field>
              <Field label={isEdit ? 'New password (leave blank to keep)' : 'Password'}>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-vt-text-muted"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </Field>
            </div>
            {isEdit && camera?.has_credentials ? (
              <div className="text-vt-12 text-vt-text-muted">
                Existing credentials are stored encrypted. Leave blank to keep them unchanged.
              </div>
            ) : null}
          </div>
        </Section>

        <Section title="Recording">
          <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-3">
            <CheckRow
              label="Recording enabled"
              checked={form.recording_enabled}
              onChange={(v) => setForm({ ...form, recording_enabled: v })}
            />
            <Field label="Storage (days)">
              <Input
                type="number"
                value={form.storage_days}
                onChange={(e) => setForm({ ...form, storage_days: e.target.value })}
                min={0}
                max={3650}
              />
            </Field>
            <Field label="NVR channel #">
              <Input
                type="number"
                value={form.nvr_channel_no}
                onChange={(e) => setForm({ ...form, nvr_channel_no: e.target.value })}
                min={0}
                max={1024}
              />
            </Field>
          </div>
          <div className="mt-vt-3">
            <Field label="Notes">
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                maxLength={500}
              />
            </Field>
          </div>
        </Section>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!form.name.trim()) {
                toast.error('Name is required');
                return;
              }
              saveMutation.mutate();
            }}
            disabled={saveMutation.isPending}
          >
            {isEdit ? 'Save changes' : 'Add camera'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CredentialsDialog({
  camera,
  onClose
}: {
  camera: Camera;
  onClose: () => void;
}): JSX.Element {
  const [revealed, setRevealed] = useState(false);
  const [creds, setCreds] = useState<{ username: string; password: string } | null>(null);
  const [loading, setLoading] = useState(false);

  async function reveal(): Promise<void> {
    setLoading(true);
    try {
      const data = await get<{ username: string; password: string }>(`/cameras/${camera.id}/credentials`);
      setCreds({ username: data.username ?? '', password: data.password ?? '' });
      setRevealed(true);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Camera credentials</DialogTitle>
          <DialogDescription>
            Decrypting credentials is audited. Only admins can reveal raw values.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-vt-3 py-vt-2">
          <Field label="Camera">
            <div className="text-vt-14">{camera.name}</div>
          </Field>
          {!revealed ? (
            <div className="flex flex-col gap-vt-2">
              <div className="text-vt-13 text-vt-text-muted">
                {camera.has_credentials ? 'Credentials are stored.' : 'No credentials configured.'}
              </div>
              <Button onClick={reveal} disabled={loading || !camera.has_credentials}>
                <ShieldAlert className="h-4 w-4" />
                Reveal (will be audited)
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2">
              <Field label="Username">
                <Input value={creds?.username ?? ''} readOnly />
              </Field>
              <Field label="Password">
                <Input value={creds?.password ?? ''} readOnly type="text" />
              </Field>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="mt-vt-3 rounded-vt-md border border-vt-border bg-vt-surface p-vt-3">
      <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-soft">
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-vt-1">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function CheckRow({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label className="inline-flex items-center gap-vt-2 text-vt-13">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
